import React, { useState } from 'react';
import { useProject } from '../../hooks/useProject';
import { useProjectPageMount } from '../../hooks/useProjectPageMount';
import { useProjectIdFromRoute } from '../../hooks/useProjectIdFromRoute';
import { Tabs } from '../../components/Tabs';
import { PageHeader } from '../../components/PageHeader';
import { Breadcrumb } from '../../components/Breadcrumb';
import { hasPermission } from '../../types/project';
import { NewCollectionModal } from './NewCollectionModal';
import { HomeTab } from './HomeTab';
import { PreProcessingTab } from './PreProcessingTab';
import { PostProcessingTab } from './PostProcessingTab';
import { CollectionsTab } from './CollectionsTab';
import { CustodiansTab } from './CustodiansTab';
import { TagsTab } from './TagsTab';
import { ReportsTab } from './ReportsTab';
import { ExportsTab } from './ExportsTab';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';

export function ProjectDetailPage() {
  const projectId = useProjectIdFromRoute();
  const { data: project, isLoading } = useProject(projectId);
  const [activeTab, setActiveTab] = useState('home');
  const [collectionsFilter, setCollectionsFilter] = useState('');
  const [addCollectionOpen, setAddCollectionOpen] = useState(false);

  useProjectPageMount(project, 'projects', 'home');

  function handleCustodianClick(name: string) {
    setCollectionsFilter(name);
    setActiveTab('collections');
  }

  const tabs = project ? [
    { id: 'home', heading: 'Home', content: <HomeTab projectId={projectId} /> },
    ...(hasPermission(project, 'VIEW_PRE_PROCESSING_TAB') ? [{ id: 'pre-processing', heading: 'Pre-Processing Overview', content: <PreProcessingTab projectId={projectId} /> }] : []),
    ...(hasPermission(project, 'VIEW_POST_PROCESSING_TAB') ? [{ id: 'post-processing', heading: 'Post-Processing Overview', content: <PostProcessingTab projectId={projectId} /> }] : []),
    ...(hasPermission(project, 'VIEW_COLLECTIONS_TAB') ? [{ id: 'collections', heading: 'Collections', content: <CollectionsTab projectId={projectId} externalFilter={collectionsFilter} /> }] : []),
    ...(hasPermission(project, 'VIEW_CUSTODIANS_TAB') ? [{ id: 'custodians', heading: 'Custodians', content: <CustodiansTab projectId={projectId} onCustodianClick={handleCustodianClick} /> }] : []),
    ...(hasPermission(project, 'VIEW_TAGS_TAB') ? [{ id: 'tags', heading: 'Tags', content: <TagsTab projectId={projectId} /> }] : []),
    ...(hasPermission(project, 'VIEW_REPORTS_TAB') ? [{ id: 'reports', heading: 'Reports', content: <ReportsTab projectId={projectId} /> }] : []),
    ...(hasPermission(project, 'VIEW_EXPORTS_TAB') ? [{ id: 'exports', heading: 'Exports', content: <ExportsTab projectId={projectId} /> }] : []),
  ] : [];

  return (
    <>
      {isLoading && <LoadingPlaceholder label="Loading project…" />}
      {project && (
        <>
          <PageHeader
            project={project}
            title={project.name}
            rightContent={hasPermission(project, 'MANAGE_COLLECTIONS') ? (
              <>
                <a
                  href="https://folio-accessid.zendesk.com/hc/en-us/articles/213127166-Adding-Data-To-Your-Project"
                  target="_blank"
                  rel="noreferrer"
                  title="Get Help"
                >
                  <i className="far fa-question-circle fa-lg" aria-hidden="true" />
                </a>
                {' '}
                <button
                  className="btn btn-primary"
                  onClick={() => setAddCollectionOpen(true)}
                >
                  Add more data to this project <i className="fas fa-plus-circle" style={{ color: '#fff' }} aria-hidden="true" />
                </button>
              </>
            ) : undefined}
          />
          <Breadcrumb items={[
            { label: 'Projects', href: '/project' },
            { label: project.name },
          ]} />

          <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

          {addCollectionOpen && project && (
            <NewCollectionModal project={project} onClose={() => setAddCollectionOpen(false)} />
          )}
        </>
      )}
    </>
  );
}
